import { permissions } from '@utilities-one/reipm-api-sdk';

import type { AccessUser, WithPermission, WithRestriction } from '@/entities/user/types';
import { hasAccess } from '@/entities/user/utils/access';

/**
 * Path-pattern → required permission table, ported from the legacy monolith's
 * `config/auth/routeAccess.ts` (spaces/permission trees are identical between
 * the monolith and reipm-api, verified by diff).
 *
 * Rules:
 * - `[param]` segments match any single path segment.
 * - A rule also guards deeper paths (`/clients` matches `/clients/x/y`)
 *   unless `exact: true`.
 * - `permission` is a dot-path into the role's permission tree; an array means
 *   "any of". Leaf permissions use the SDK `permissions` constants; plain
 *   strings are deliberate *subtree* checks (e.g. `'jobs.view'` grants when any
 *   `jobs.view.*` variant is set — the constants object has no string there).
 * - `restriction` (none ported today) may further gate by user shape.
 *
 * UG-4498 wires this into the router via {@link checkRouteAccess}
 * (`false` → `<Navigate to='/404' replace />`).
 */
export type RouteAccessRule = WithRestriction<WithPermission<{ pathname: string; exact?: boolean }>>;

export const routeAccess: RouteAccessRule[] = [
  { pathname: '/jobs', permission: 'jobs.view' },
  { pathname: '/jobs/create', permission: permissions.jobs.create },
  { pathname: '/user-assessment', permission: permissions.users.userAssessments.canAssessUser },

  { pathname: '/clients', permission: permissions.clients.view },
  { pathname: '/clients/[id]/update', permission: permissions.clients.update },
  { pathname: '/clients/[id]/codes', permission: permissions.clients.codes.view },
  { pathname: '/clients/[id]/codes/[codeId]/update', permission: permissions.clients.codes.update },
  { pathname: '/clients/[id]/codes/create', permission: permissions.clients.codes.create },
  { pathname: '/clients/[id]/codeAddons', permission: permissions.clients.codeAddons.view },
  { pathname: '/clients/[id]/codeAddons/[codeAddonId]/update', permission: permissions.clients.codeAddons.update },
  { pathname: '/clients/[id]/codeAddons/create', permission: permissions.clients.codeAddons.create },
  { pathname: '/clients/[id]/photoTypes', permission: permissions.clients.photoTypes.view },
  { pathname: '/clients/[id]/photoTypes/[photoTypeId]/update', permission: permissions.clients.photoTypes.update },
  { pathname: '/clients/[id]/photoTypes/create', permission: permissions.clients.photoTypes.create },
  { pathname: '/clients/create', permission: permissions.clients.create },
  { pathname: '/clients/[id]/qcTemplate', permission: permissions.qcTemplate.view },
  { pathname: '/clients/[id]/userAssessmentTemplate', permission: permissions.userAssessmentsTemplates.view },
  { pathname: '/clients/[id]/jobTypes', permission: permissions.clients.jobTypes.view },
  { pathname: '/clients/[id]/jobTypes/[jobTypeId]/update', permission: permissions.clients.jobTypes.update },
  { pathname: '/clients/[id]/jobTypes/create', permission: permissions.clients.jobTypes.create },
  { pathname: '/clients/[id]/contracts', permission: permissions.clients.contracts.view },
  { pathname: '/clients/[id]/contracts/create', permission: permissions.clients.contracts.create },
  { pathname: '/clients/[id]/contracts/[contractId]/update', permission: permissions.clients.contracts.update },
  {
    pathname: '/clients/[id]/contracts/[contractId]/jobSpecifications',
    permission: permissions.clients.jobSpecifications.view,
  },
  {
    pathname: '/clients/[id]/contracts/[contractId]/jobSpecifications/create',
    permission: permissions.clients.jobSpecifications.create,
  },
  {
    pathname: '/clients/[id]/contracts/[contractId]/jobSpecifications/[jobSpecificationId]/update',
    permission: permissions.clients.jobSpecifications.update,
  },
  { pathname: '/clients/[id]/jobSpecifications', permission: permissions.clients.jobSpecifications.view },

  // Leaf, not the module group: a `false` override under `companies.*` would still make the group truthy.
  { pathname: '/companies', permission: permissions.companies.view },
  { pathname: '/companies/create', permission: permissions.companies.create },
  { pathname: '/companies/[id]/update', permission: permissions.companies.update },
  { pathname: '/companies/[id]/employees', permission: permissions.companies.employees.view },
  { pathname: '/companies/[id]/clients', permission: permissions.companies.clients.view },
  { pathname: '/companies/[id]/clients/connect', permission: permissions.companies.clients.attachment },
  {
    pathname: '/companies/[id]/clients/[clientId]/companyContracts',
    permission: permissions.companies.contracts.view,
  },
  {
    pathname: '/companies/[id]/clients/[clientId]/companyContracts/create',
    permission: permissions.companies.contracts.create,
  },
  {
    pathname: '/companies/[id]/clients/[clientId]/companyContracts/[companyContractId]/update',
    permission: permissions.companies.contracts.update,
  },
  {
    pathname: '/companies/[id]/clients/[clientId]/companyContracts/[companyContractId]/jobSpecifications',
    permission: permissions.companies.jobSpecifications.view,
  },
  {
    pathname: '/companies/[id]/clients/[clientId]/companyContracts/[companyContractId]/jobSpecifications/create',
    permission: permissions.companies.jobSpecifications.create,
  },
  {
    pathname:
      '/companies/[id]/clients/[clientId]/companyContracts/[companyContractId]/jobSpecifications/[companyJobSpecificationId]/update',
    permission: permissions.companies.jobSpecifications.update,
  },
  { pathname: '/companies/[id]/history', permission: permissions.companies.view },

  { pathname: '/users', permission: 'users.view' },
  { pathname: '/users/[id]/update', permission: permissions.users.update },
  { pathname: '/users/[id]/permissions', permission: permissions.users.update },
  { pathname: '/users/create', permission: 'users.create' },

  { pathname: '/markets', permission: permissions.markets.view },
  { pathname: '/markets/[id]/update', permission: permissions.markets.update },
  { pathname: '/markets/create', permission: permissions.markets.create },
  { pathname: '/markets/[id]/awaTemplate', permission: permissions.awaTemplate.view },

  { pathname: '/roles', permission: permissions.settings.roles.view },
  { pathname: '/roles/create', permission: permissions.settings.roles.create },
  { pathname: '/roles/[id]/update', permission: permissions.settings.roles.update },

  { pathname: '/sessions', permission: permissions.settings.sessions.view },

  { pathname: '/awa/create', permission: permissions.awaResponse.create },
  { pathname: '/awa-reports', permission: 'awaResponse.view' },
  { pathname: '/awa/[id]/view', permission: 'awaResponse.view' },
  { pathname: '/jobs/[jobId]/awa', permission: 'awaResponse.view' },

  { pathname: '/qc-reports', permission: permissions.qcResponse.view },
  { pathname: '/qc-reports/[qcId]/view', permission: permissions.qcResponse.view },
  { pathname: '/qc-responses-reports', permission: permissions.qcResponse.view },
  { pathname: '/qc-responses-reports/[qcId]/view', permission: permissions.qcResponse.view },

  { pathname: '/errors', permission: permissions.errors.view },
  { pathname: '/errors/[errorId]/view', permission: permissions.errors.view },

  { pathname: '/tasks', permission: 'tasks.view' },
  { pathname: '/tasks/create', permission: permissions.tasks.manage },
  { pathname: '/tasks/[id]/update', permission: permissions.tasks.manage },
];

/**
 * Does `path` fall under `routePattern`? `[param]` matches one segment; unless
 * `exact`, deeper paths also match (legacy/gpm behavior).
 */
export const matchRoute = (path: string, routePattern: string, exact = false): boolean => {
  const regexPattern = new RegExp(
    '^' + routePattern.replace(/\[.*?\]/g, '[^/]+').replace(/\//g, '\\/') + (exact ? '' : '(?:/.*)?') + '$'
  );
  return regexPattern.test(path);
};

/**
 * `true` ⇔ `user` may open `pathname`.
 *
 * A pathname with no matching rule is public-to-any-authenticated-user.
 * A matching rule denies when the user lacks its `permission` or fails its
 * `restriction`. With no user (`null`), any matched guarded route is denied —
 * defensive; in practice AccessProvider never lets an unauthenticated session
 * reach the router.
 */
export const checkRouteAccess = (pathname: string, user: AccessUser | null | undefined): boolean => {
  return !routeAccess.some(({ pathname: routePath, permission, restriction, exact }) => {
    if (!matchRoute(pathname, routePath, exact)) return false;
    if (!user) return true;

    const lacksPermission = permission !== undefined && !hasAccess(user, permission);
    const failsRestriction = restriction !== undefined && !restriction(user);
    return lacksPermission || failsRestriction;
  });
};
